import { of ,  Observable, defer, never } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { flatMap, publishReplay, refCount, take, catchError, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TenantService {
    private cachedEmailTenantFetches = {};
    private cachedUnauthTenantInfo: any;
    private cachedTenantInfo: any;

    constructor(private api: ApiService){}
    
    starterTenant = (parentTenantId:string, parentTitle: string, hideHelp: string, allowedSources: any, limitProductSources: any) => ({

        settings: {
            title: parentTitle,
            streamsEmailDomain: 'email.attensa.net',
            briefingsFromAddress: 'briefings@attensa.com',
            briefingsReplyToAddress: 'info@attensa.com',
            newUserRoleType: 'STREAM_CREATOR',
            topicInviteEnabled: 'true',
            autoTopicPublish: 'true',
            noStreamBriefings: 'true',
            hideCategories: 'true',
            excludeEmailFilter: 'attensa.com',
            limitProductSources: limitProductSources,
            allowedSources: allowedSources ? JSON.stringify(allowedSources) : undefined,
            reportingExports: {
                item_viewed_topic: true,
                item_viewed_source: true,
                item_topic_link_clicked: true,
                item_source_link_clicked: true,
                briefing_activity: true,
                digest_activity: true,
                url_changed: true,
                hideHelp: hideHelp
            },
            defaultPages: { 'DEFAULT': '/client' }
        }
    });

    saveTenant(tenant) {
        return this.api.post('/ws/tenant', tenant)
        .pipe(
            tap(() => {
                this.cachedUnauthTenantInfo = undefined;
                this.cachedTenantInfo = undefined; 
            })
        );
    }

    saveSettings(settings) {
        return this.api.put('/ws/tenant/settings', settings)
        .pipe(
            tap(() => {
                this.cachedUnauthTenantInfo = undefined;
                this.cachedTenantInfo = undefined; 
            })
        );
    };

    isAutoPublishTopicTenant = () => 
        this.getUnauthTenantInfo().pipe(flatMap(tenant => of(tenant.settings.autoTopicPublish && tenant.settings.autoTopicPublish === 'true')));

    createTenant = tenant => this.api.post(`/ws/tenant/subtenant`, tenant);
    
    updateTenant = tenant => this.api.put(`/ws/tenant/${tenant.id}`, tenant);

    /*
        id
        availableCatalogs
        hostname
        name
        partner
        status
        settings.allowedSources
        settings.autoBriefingPublish
        settings.autoTopicPublish
        settings.blockIntercom
        settings.clientDigestMenuOptions
        settings.defaultPages
        settings.enableAdvancedFilters
        settings.eventWriteUrl
        settings.hideCategories
        settings.hideHelp
        settings.labels
        settings.limitProductSources
        settings.noInitialDigest
        settings.paymentType
        settings.productOversubscribedMessage
        settings.publicTopics
        settings.selfSignupConfiguration
        settings.signupConfirmationRedirect
        settings.showFollowers
        settings.showManuallySelectSources
        settings.title
    */
    getUnauthTenantInfo = (): Observable<any> => {
        if (!this.cachedUnauthTenantInfo) {
            this.cachedUnauthTenantInfo = defer(() => this.api.get('/ws/unauthTenant/unauthTenantInfo')).pipe(publishReplay(1, 60000), refCount(), take(1));
        }

        return this.cachedUnauthTenantInfo;
    };

    getCurrentTenant = (): Observable<any> => {        
        if (!this.cachedTenantInfo) {
            this.cachedTenantInfo = defer(() => this.api.get('/ws/tenant/current')).pipe(publishReplay(1, 60000), refCount(), take(1));
        }

        return this.cachedTenantInfo;
    };

    tenantHostExists = (host: string):Observable<any>  => this.api.get('/ws/ua/tenant/', this.api.paramify({ tenantHost: host }));

    getRelatedTenancies = (emailAddress: string): Observable<any> => {
        if (!this.cachedEmailTenantFetches[emailAddress]) {
            const cacheTime = 1000 * 60 * 5; // Five minutes
            this.cachedEmailTenantFetches[emailAddress] = defer(() => this.api.get('/ws/ua/tenant/related/', this.api.paramify({ emailAddress })).pipe(catchError(e => never()), publishReplay(1, cacheTime), refCount(), take(1)));
        }
        return this.cachedEmailTenantFetches[emailAddress];
    };

    getSubtenancies = (params?):Observable<any> => this.api.get(`/ws/tenant/subtenanancies`, this.api.paramify(params));

    getSubtenant = (subTenantId:string) => this.api.get(`/ws/tenant/subtenant/${subTenantId}`);

    getCatalogMap() {
        return this.api.get('/ws/tenant/catalogs');
    }

    getCatalogs(params?) {
        return this.api.get('/ws/catalogs', this.api.paramify(params));
    }

    addCatalog(catalog): Observable<any> {
        return this.api.post(`/ws/catalogs`, catalog);
    }

    deleteCatalog(catalogId: string): Observable<any> {
        return this.api.delete(`/ws/catalogs/${catalogId}`);
    }

    getCatalog(catalogId: string): Observable<any> {
        return this.api.get(`/ws/catalogs/${catalogId}`)
    }

    getCatalogTags(): Observable<any> {
        return this.api.get(`/ws/catalogs/tags`);
    }

    getCatalogTagStreams(tagId: string, params): Observable<any> {
        return this.api.get(`/ws/catalogs/tags/${tagId}/streams`, this.api.paramify(params));
    }

    createCatalogTag(tag: { label: string }): Observable<any> {
        return this.api.post(`/ws/catalogs/tags`, tag);
    }

    updateCatalogTag(tag) {
        return this.api.put('/ws/catalogs/tags/' + tag.id, tag);
    }

    deleteCatalogTag(tag) {
        return this.api.delete(`/ws/catalogs/tags/${tag.id}`, tag);
    }

    getCatalogStreams(catalogId: string, params): Observable<any> {
        return this.api.get(`/ws/catalogs/${catalogId}/streams`, this.api.paramify(params));
    }

    addStreamsToCatalog(catalogId: string, streamIds: string[]): Observable<any> {
        return this.api.post(`/ws/catalogs/${catalogId}/streams}`, streamIds);
    }

    deleteStreamFromCatalog(catalogId: string, streamId: string): Observable<any> {
        return this.api.delete(`/ws/catalogs/${catalogId}/streams/${streamId}`);
    }

    getStreamTags() {
        return this.api.get('/ws/tenant/streamTags');
    }

    deleteStreamTag(tag) {
        return this.api.delete(`/ws/tenant/streamTags/${tag.id}`, tag);
    }

    createStreamTag(tag: { label: string }) {
        return this.api.post('/ws/tenant/streamTags', tag);
    }

    updateStreamTag(tag) {
        return this.api.put('/ws/tenant/streamTags/' + tag.id, tag);
    }

    fetchPlan = () => this.api.get('/ws/tenant/plan');

    fetchSubscriptionCustomer = () => this.api.get('/ws/tenant/subscription/customer');
    
    fetchSubscriptionUpcomingInvoice = () => this.api.get('/ws/tenant/subscription/upcomingInvoice');
    
    fetchSubscriptionInvoices = () => this.api.get('/ws/tenant/subscription/invoices');

    makeSubscriptionCardDefault = (card) => this.api.post('/ws/tenant/subscriptionCustomer/defaultCard', card);

    removeSubscriptionCard = (cardId) => this.api.delete(`/ws/tenant/subscription/card/${cardId}`);

    createPaymentMethod(token) {
        return this.api.post(`/ws/tenant/paymentMethod`, token)
        .pipe(
            tap(() => {
                this.cachedUnauthTenantInfo = undefined;
                this.cachedTenantInfo = undefined; 
            })
        );
    }

    saveDigestCommentaries(commentaries) {
        return this.api.put('/ws/tenant/digestCommentaries', commentaries)
        .pipe(
            tap(() => this.cachedUnauthTenantInfo = undefined),
            tap(() => this.cachedTenantInfo = undefined)
        );
    }
};
